import styled from "styled-components";
import { Modal } from "../../../design-system/components/composite/Modal/Modal";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { Button } from "../../../design-system/components/primitives/Button/Button";
import { shadows } from "../../../design-system/tokens/shadows";
import { borderRadius } from "../../../design-system/tokens/borders";
import { typographyMixin, fontWeights, fontSizes } from "../../../design-system/tokens/typography";

// ─── Root body — fills the full ModalBody (flex:1) height, no internal scroll ─

/** F18: 420px dialog, radius 20. */
export const ExportModalShell = styled(Modal)`
  border-radius: ${borderRadius[20]};
`;

export const ExportBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  padding: ${spacing[2]} ${spacing[6]} ${spacing[6]};
  background-color: ${rawColors.structural.white};
`;

export const ExportTitle = styled.h2`
  margin: 0;
  ${typographyMixin("heading", "h3")}
  font-weight: ${fontWeights.semibold};
  color: ${rawColors.text.highEmphasis};
`;

// ─── Panels (plain stacks; the card is the dialog) ────────────────────────────

export const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

export const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  min-width: 0;
`;

// ─── Section titles ───────────────────────────────────────────────────────────

export const SectionTitle = styled.p`
  margin: 0 0 ${spacing[2]} 0;
  ${typographyMixin("caption", 3)}
  font-weight: ${fontWeights.bold};
  color: ${rawColors.text.highEmphasis};
`;

export const RightSectionTitle = styled.p`
  margin: 0 0 ${spacing[2]} 0;
  ${typographyMixin("caption", 3)}
  font-weight: ${fontWeights.bold};
  color: ${rawColors.text.highEmphasis};
`;

// ─── Segmented control (Format, Slide size) ───────────────────────────────────

export const SegmentedControl = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]};
  border-radius: ${borderRadius.lg};
  background-color: ${rawColors.grey[100]};
`;

export const SegmentOption = styled.button<{ $active?: boolean }>`
  /* Never clip a label ("Google Drive"): size to content, share the rest equally. */
  flex: 1 1 auto;
  min-width: max-content;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1.5]};
  white-space: nowrap;
  padding: ${spacing[2]} ${spacing[3]};
  border: none;
  border-radius: ${borderRadius.md};
  ${typographyMixin("body", 3)}
  font-size: ${fontSizes[13]};
  font-weight: ${fontWeights.semibold};
  cursor: pointer;
  background-color: ${({ $active }) => ($active ? rawColors.structural.white : "transparent")};
  color: ${({ $active }) => ($active ? rawColors.text.highEmphasis : rawColors.text.mediumEmphasis)};
  box-shadow: ${({ $active }) => ($active ? shadows.elevation[1] : "none")};

  &:disabled {
    cursor: not-allowed;
    /* readable, not faded: the peach Coming soon tag says why (F18) */
    color: ${rawColors.text.mediumEmphasis};
  }
`;

export const SegmentNote = styled.p`
  margin: ${spacing[1]} 0 0;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
`;

export const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing[3]};
`;

export const OutlinePillButton = styled(Button)`
  &&& {
    border-radius: ${borderRadius.full};
    white-space: nowrap;
  }
`;

export const PrimaryPillButton = styled(Button)`
  &&& {
    border-radius: ${borderRadius.full};
    white-space: nowrap;
    background-color: ${rawColors.primary[500]};
    border-color: ${rawColors.primary[500]};
    color: ${rawColors.text.white};

    &:hover:not(:disabled) {
      background-color: ${rawColors.primary[600]};
      border-color: ${rawColors.primary[600]};
    }
  }
`;

export const PreviewPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

// ─── Slide size ────────────────────────────────────────────────────────────────

export const SlideSizeButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  padding: 4px;
  background-color: ${rawColors.structural.white};
`;

export const SizButton = styled.button<{ $active?: boolean }>`
  flex: 1;
  padding: ${spacing[2]} 0;
  border: none;
  border-radius: ${borderRadius.md};
  background-color: ${({ $active }) => ($active ? "#F4F0FF" : "transparent")};
  color: ${({ $active }) =>
    $active ? rawColors.text.highEmphasis : rawColors.text.mediumEmphasis};
  ${typographyMixin("body", 3)}
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${({ $active }) => ($active ? "#F4F0FF" : rawColors.grey[50])};
  }
`;

// ─── Export summary ───────────────────────────────────────────────────────────

export const ExportSummaryBox = styled.div`
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.md};
  overflow: hidden;
  background-color: #F4F0FF;
`;

export const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing[2]} ${spacing[3]};
`;

export const SummaryLabel = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const SummaryValue = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
`;

// ─── Template dropdown (custom overlay on native <select>) ────────────────────

export const TemplateDropdownWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.md};
  background-color: ${rawColors.structural.white};
  cursor: pointer;

  &:hover {
    border-color: ${rawColors.stroke[300]};
  }
`;

export const TemplateGradientIcon = styled.div`
  width: 32px;
  height: 22px;
  border-radius: ${borderRadius.sm};
  background: linear-gradient(135deg, #f97316 0%, #a855f7 100%);
  flex-shrink: 0;
`;

export const TemplateDropdownLabel = styled.span`
  flex: 1;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
`;

export const TemplateDropdownChevron = styled.span<{ $open?: boolean }>`
  color: ${rawColors.text.mediumEmphasis};
  display: flex;
  align-items: center;
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0deg)")};
`;

export const TemplateDropdownSelect = styled.select`
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
`;

// Template dropdown menu
export const TemplateDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.md};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin-top: ${spacing[1]};
  min-width: 250px;
`;

export const TemplateOptionItem = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  width: 100%;
  padding: ${spacing[2]} ${spacing[3]};
  border: none;
  background-color: ${({ $selected }) =>
    $selected ? rawColors.primary[50] : rawColors.structural.white};
  cursor: pointer;
  transition: background-color 0.15s;
  border-bottom: 1px solid ${rawColors.stroke[100]};
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${rawColors.grey[50]};
  }
`;

export const TemplateOptionIcon = styled.div<{ $color: string }>`
  width: 32px;
  height: 24px;
  border-radius: ${borderRadius.sm};
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const TemplateOptionLabel = styled.span`
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
  font-weight: 500;
  flex: 1;
`;

// ─── Preview area: thumbnail strip (left) + big preview (right) ───────────────

export const PreviewArea = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: ${spacing[3]};
  min-height: 0;
  overflow: hidden;
  margin-bottom: 10px;
`;

// Each thumbnail fills equal vertical space — no fixed heights needed
export const ThumbnailStrip = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  height: 100%;
  overflow: hidden;
`;

export const Thumbnail = styled.div<{ $selected?: boolean }>`
  flex: 1;
  border: 2px solid ${({ $selected }) =>
    $selected ? rawColors.primary[500] : rawColors.stroke[200]};
  border-radius: ${borderRadius.sm};
  overflow: hidden;
  cursor: pointer;
  background-color: ${rawColors.grey[50]};
  transition: border-color 0.15s;

  &:hover {
    border-color: ${rawColors.primary[300]};
  }
`;

export const ThumbnailInner = styled.div`
  width: 100%;
  height: 100%;
  padding: ${spacing[1]};
  display: flex;
  flex-direction: column;
  gap: 3px;
  overflow: hidden;
`;

export const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 2px;
`;

export const ThumbnailBar = styled.div<{ $width?: string; $color?: string }>`
  height: 4px;
  border-radius: 2px;
  background-color: ${({ $color }) => $color || rawColors.primary[200]};
  width: ${({ $width }) => $width || "100%"};
`;

export const ThumbnailBlock = styled.div`
  flex: 1;
  background-color: ${rawColors.grey[100]};
  border-radius: 2px;
`;

// ─── Big slide preview ────────────────────────────────────────────────────────

export const BigPreview = styled.div`
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: 12px;
  background-color: ${rawColors.structural.white};
  padding: ${spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  overflow: hidden;
  height: 100%;
  box-sizing: border-box;
`;

export const BigPreviewTitle = styled.h4`
  margin: 0;
  ${typographyMixin("subtitle", 1)}
  color: ${rawColors.text.highEmphasis};
  flex-shrink: 0;
`;

export const BigPreviewContent = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[3]};
  min-height: 0;
  overflow: hidden;
  background-color: ${rawColors.grey[50]};
  border-radius: ${borderRadius.md};
  padding: ${spacing[3]};
`;

// Left column: chart image card
export const SlideChartCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  overflow: hidden;
`;

export const SlideChartLabel = styled.span`
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
  font-weight: 600;
  flex-shrink: 0;
`;

export const SlideChartImageWrapper = styled.div`
  flex: 1;
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.md};
  background-color: ${rawColors.structural.white};
  overflow: hidden;
  padding: ${spacing[2]};
`;

export const BigPreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: ${borderRadius.sm};
`;

// Right column: insight text
export const SlideInsightSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  overflow: hidden;
  padding: ${spacing[2]};
  justify-content: center;
`;

export const SlideInsightTitle = styled.p`
  margin: 0;
  ${typographyMixin("subtitle", 2)}
  color: ${rawColors.text.highEmphasis};
  font-weight: 700;
  line-height: 1.4;
  flex-shrink: 0;
`;

export const SlideInsightText = styled.p`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  line-height: 1.6;
  overflow: hidden;
`;

export const PreviewDivider = styled.div`
  height: 1px;
  background-color: ${rawColors.stroke[200]};
  margin-top: ${spacing[4]};
  flex-shrink: 0;
`;

export const ChartSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  overflow: hidden;
`;

export const ChartLabel = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  font-weight: 600;
  flex-shrink: 0;
`;

export const ChartRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

export const ChartRowLabel = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  width: 54px;
  flex-shrink: 0;
`;

export const ChartBarTrack = styled.div`
  flex: 1;
  height: 8px;
  background-color: ${rawColors.grey[100]};
  border-radius: 2px;
  overflow: hidden;
`;

export const ChartBarFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background-color: ${rawColors.primary[300]};
  border-radius: 2px;
`;

export const ChartRowValue = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  width: 28px;
  text-align: right;
`;

export const InsightSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  padding: ${spacing[3]};
  background-color: ${rawColors.grey[50]};
  border-radius: ${borderRadius.sm};
  overflow: hidden;
`;

export const InsightTitle = styled.p`
  margin: 0;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
  font-weight: 600;
  line-height: 1.4;
  flex-shrink: 0;
`;

export const InsightText = styled.p`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  line-height: 1.5;
  overflow: hidden;
`;

// ─── PDF page preview ─────────────────────────────────────────────────────────

export const PdfPreviewArea = styled.div`
  flex: 1;
  max-height: 22rem;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  align-items: stretch;
  padding: ${spacing[3]} ${spacing[2]};
  background-color: ${rawColors.grey[50]};
  border-radius: ${borderRadius.md};
  min-height: 0;
  box-sizing: border-box;
`;

export const PdfPage = styled.div<{ $flush?: boolean }>`
  width: 100%;
  ${({ $flush }) => ($flush ? "padding: 0; overflow: hidden;" : "")}
  background-color: ${rawColors.structural.white};
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  padding: ${({ $flush }) => ($flush ? 0 : `${spacing[5]} ${spacing[4]}`)};
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  flex-shrink: 0;
`;

export const PdfPageTitle = styled.h4`
  margin: 0;
  padding-bottom: ${spacing[2]};
  ${typographyMixin("subtitle", 1)}
  color: ${rawColors.text.highEmphasis};
  border-bottom: 2px solid ${rawColors.primary[500]};
  flex-shrink: 0;
`;

export const PdfPageChartArea = styled.div`
  width: 100%;
  background-color: ${rawColors.grey[50]};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.md};
  padding: ${spacing[2]} ${spacing[3]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;

export const PdfPageChartLabel = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
  font-weight: 600;
`;

export const PdfPageChartImg = styled.img`
  width: 100%;
  max-width: 100%;
  height: 110px;
  object-fit: contain;
  display: block;
`;

export const PdfPageInsightTitle = styled.p`
  margin: 0;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
  font-weight: 600;
  line-height: 1.4;
`;

export const PdfPageInsightText = styled.p<{ $muted?: boolean }>`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${({ $muted }) => ($muted ? rawColors.text.lowEmphasis : rawColors.text.mediumEmphasis)};
  line-height: 1.6;
`;

export const PdfPageNumber = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.lowEmphasis};
  text-align: center;
  margin-top: ${spacing[1]};
`;
